import React, { useContext, useEffect, useState } from 'react'
import { ReservationContext } from '../../context/ReservationContext'
import { LoadingSkeleton } from '../LoadingSkeleton'
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import { archivePrice, unarchivePrice, createPrice } from '../backend'
import { Add, Archive as ArchiveIcon, Unarchive as UnarchiveIcon } from '@mui/icons-material'
import PropTypes from 'prop-types'

export default function PriceSelection() {
  const { shows } = useContext(ReservationContext)
  const [selectedShowId, setSelectedShowId] = useState(3)
  // default showId = 3 = ex-voto
  const [prices, setPrices] = useState([])
  const [loading, setLoading] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [newPrice, setNewPrice] = useState({ nickname: '', amount: '' })
  const [snackMessage, setSnackMessage] = useState({ message: '', severity: 'success' })

  const selectedShow = shows.find((s) => s.id === selectedShowId)

  const activePrices = prices.filter((p) => p.active)
  const archivedPrices = prices.filter((p) => !p.active)

  useEffect(() => {
    setLoading(true)
    const show = shows.find((s) => s.id === selectedShowId)
    setPrices(show?.prices || [])
    setLoading(false)
  }, [selectedShowId])

  const handleArchive = async (priceId) => {
    setLoading(true)
    try {
      await archivePrice(priceId)
      setPrices((prev) => prev.map((p) => (p.id === priceId ? { ...p, active: false } : p)))
      setSnackMessage({ message: 'Prix archivé', severity: 'success' })
    } catch {
      setSnackMessage({ message: "Erreur lors de l'archivage", severity: 'error' })
    }
    setLoading(false)
  }

  const handleUnarchive = async (priceId) => {
    setLoading(true)
    try {
      await unarchivePrice(priceId)
      setPrices((prev) => prev.map((p) => (p.id === priceId ? { ...p, active: true } : p)))
      setSnackMessage({ message: 'Prix désarchivé', severity: 'success' })
    } catch {
      setSnackMessage({ message: 'Erreur lors du désarchivage', severity: 'error' })
    }
    setLoading(false)
  }

  const handleAddPrice = async () => {
    if (!newPrice.nickname || !newPrice.amount || !selectedShow?.productId) return
    try {
      setLoading(true)
      const createdPrice = await createPrice({
        product_id: selectedShow.productId,
        amount: parseFloat(newPrice.amount),
        nickname: newPrice.nickname
      })
      setDialogOpen(false)
      setNewPrice({ nickname: '', amount: '' })
      setPrices((prev) => [
        ...prev,
        { id: createdPrice.price.id, price: createdPrice.price.price, name: createdPrice.price.name, active: createdPrice.price.active }
      ])
      setSnackMessage({ message: 'Prix ajouté', severity: 'success' })
    } catch {
      setSnackMessage({ message: 'Erreur lors de la création du prix', severity: 'error' })
    }
    setLoading(false)
  }

  if (!shows.length) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant='body1' color='text.secondary'>
          Aucun spectacle avec configuration Stripe
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Select
        value={selectedShowId}
        label='Spectacle'
        onChange={(e) => {
          const showId = e.target.value
          if (showId) {
            setSelectedShowId(showId)
          }
        }}
        variant='standard'
        sx={{ minWidth: 200, mb: 2 }}
      >
        {shows.map((show) => (
          <MenuItem key={show.id} value={show.id}>
            {show.title}
          </MenuItem>
        ))}
      </Select>

      {loading ? (
        <LoadingSkeleton text={'Chargement des prix ...'} />
      ) : (
        <>
          <PriceSection
            title='Prix actifs'
            prices={activePrices}
            onArchive={handleArchive}
            addPrice={
              <Button variant='outlined' startIcon={<Add />} onClick={() => setDialogOpen(true)} size='small'>
                Ajouter un prix
              </Button>
            }
          />
          {archivedPrices.length > 0 && <PriceSection title='Prix archivés' prices={archivedPrices} onArchive={handleUnarchive} />}
        </>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Ajouter un prix</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={2}>
            <TextField
              label='Nom du prix'
              value={newPrice.nickname}
              onChange={(e) => setNewPrice((prev) => ({ ...prev, nickname: e.target.value }))}
              fullWidth
              placeholder='ex: Adulte, Enfant, Groupe'
            />
            <TextField
              label='Prix en EUR'
              type='number'
              value={newPrice.amount}
              onChange={(e) => setNewPrice((prev) => ({ ...prev, amount: e.target.value }))}
              fullWidth
              InputProps={{ inputProps: { min: 0, step: 0.5 } }}
              placeholder='ex: 12.50'
            />
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Annuler</Button>
          <Button variant='contained' onClick={handleAddPrice} disabled={!newPrice.nickname || !newPrice.amount}>
            Ajouter
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackMessage.message}
        autoHideDuration={4000}
        onClose={() => setSnackMessage({ message: '', severity: 'success' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackMessage({ message: '', severity: 'success' })}
          severity={snackMessage.severity}
          variant='filled'
          sx={{ width: '100%' }}
        >
          {snackMessage.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

function PriceSection({ title, prices, onArchive, addPrice }) {
  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        mb: 2,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider',
          width: '80%'
        }}
      >
        <Typography variant='h6' fontWeight='bold' color='primary'>
          {title}
        </Typography>
        {addPrice && addPrice}
      </Box>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5, textAlign: 'center', fontStyle: 'italic' }}>
        Clique sur l&apos;icône pour {title === 'Prix actifs' ? 'archiver' : 'désarchiver'} ce prix.
      </Typography>
      {prices.length === 0 ? (
        <Typography variant='body2' color='text.secondary' sx={{ fontStyle: 'italic' }}>
          {title === 'Prix actifs' ? 'Aucun prix actif' : 'Aucun prix archivé'}
        </Typography>
      ) : (
        <Stack spacing={1}>
          {prices.map((price) => (
            <PriceRow key={price.id} price={price} onArchive={onArchive} />
          ))}
        </Stack>
      )}
    </Card>
  )
}

function PriceRow({ price, onArchive }) {
  return (
    <Stack
      direction='row'
      alignItems='center'
      justifyContent='space-between'
      sx={{ px: 1, py: 0.5, borderRadius: 1, '&:hover': { backgroundColor: 'action.hover' } }}
    >
      <Stack direction='row' spacing={1.5} alignItems='center'>
        <Typography variant='body1'>{price.name}</Typography>
        <Chip
          label={price.active ? 'Actif' : 'Archivé'}
          size='small'
          sx={{
            backgroundColor: price.active ? 'success.light' : 'grey.300',
            color: price.active ? 'success.dark' : 'grey.700',
            fontWeight: 600
          }}
        />
      </Stack>
      <Stack direction='row' alignItems='center' spacing={1} sx={{ ml: 1 }}>
        <Typography variant='body1' fontWeight={600}>
          {price.price.toFixed(2)} EUR
        </Typography>
        <IconButton size='small' onClick={() => onArchive(price.id)} sx={{ color: 'text.secondary' }}>
          {price.active ? <ArchiveIcon fontSize='small' /> : <UnarchiveIcon fontSize='small' />}
        </IconButton>
      </Stack>
    </Stack>
  )
}

PriceRow.propTypes = {
  price: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    active: PropTypes.bool.isRequired
  }).isRequired,
  onArchive: PropTypes.func.isRequired
}

PriceSection.propTypes = {
  title: PropTypes.string.isRequired,
  prices: PropTypes.arrayOf(PriceRow.propTypes.price).isRequired,
  onArchive: PropTypes.func.isRequired,
  addPrice: PropTypes.node
}
